// These consts are identical to the ones in common.constants.ts and should be synced with them
import {
  APEX_CLASS,
  APEX_TRIGGER,
  APPROVAL_PROCESS,
  EMAIL_TEMPLATE,
  FIELD,
  FLOW,
  LAYOUT,
  OBJECT,
  PATH_ASSISTANT,
  RECORD_TYPE,
  REPORT,
  VALIDATION_RULE,
  WORKFLOW_RULE,
} from './parser';

// TODO SWEEP-8247 - allow shared-with-client directory to recognize common directory in some way.
//  Once done, we can get these two consts from common, as they duplicate what we have there
export const CPQ_NAMESPACE_PREFIXES = ['SBQQ__', 'sbaa__'];
const CPQ_OBJECTS = ['Product2', 'Pricebook2', 'PricebookEntry'];

export enum FlowTypes {
  SCREEN = 'Screen Flow',
  AUTO_LAUNCHED = 'Autolaunched Flow',
  RECORD_TRIGGERED = 'Record-Triggered Flow',
  SCHEDULED_TRIGGERED = 'Schedule-Triggered Flow',
  PLATFORM_EVENT = 'Platform Event Flow',
  ORCHESTRATION = 'Record-Triggered Orchestration',
}

export type AuditData = {
  createdBy: string;
  createdAt: string;
  changedBy: string;
  changedAt: string;
};

export interface ElementPropertiesMap {
  [APEX_CLASS]: ApexClassProperties;
  [APEX_TRIGGER]: ApexTriggerProperties;
  [APPROVAL_PROCESS]: ApprovalProcessProperties;
  [EMAIL_TEMPLATE]: EmailTemplateProperties;
  [FIELD]: BaseFieldProperties;
  [FLOW]: FlowProperties;
  [LAYOUT]: LayoutProperties;
  [PATH_ASSISTANT]: PathProperties;
  [RECORD_TYPE]: RecordTypeProperties;
  [REPORT]: ReportProperties;
  [VALIDATION_RULE]: ValidationRuleProperties;
  [WORKFLOW_RULE]: WorkflowRuleProperties;
}

type KnownElementProperties = ElementPropertiesMap[Exclude<
  keyof ElementPropertiesMap,
  typeof FIELD
>];

export type ConfigurationProperties =
  | KnownElementProperties
  | CpqRecordProperties
  | BaseElementProperties;

export function isElementProperty<K extends keyof ElementPropertiesMap>(
  prop: { type: string },
  type: K,
): prop is ElementPropertiesMap[K] {
  return prop.type === type;
}

export interface ObjectProperties extends BaseElementProperties {
  type: typeof OBJECT;
}

export type ConfigurationOrFieldProperties =
  | ConfigurationProperties
  | ElementPropertiesMap[typeof FIELD];

export type ElementProperties = ConfigurationProperties | ObjectProperties | BaseFieldProperties;

export type ElementPropertiesWithMatchData = ElementProperties & MatchData;

export interface BaseFieldProperties {
  type: typeof FIELD;
  id: string;
  metadataId: string;
  name: string;
  label: string;
  fieldType: string; // TODO SWEEP-8247 - replace to NaclFieldType
  description?: string;
  helpText?: string;
  formula?: string;
  picklistValues?: { name: string; label: string }[]; // TODO SWEEP-8247 - change to NameProperties
  referencedObjects?: string[];
  isMandatory: boolean;
  annotations: AuditData;
  link: string;
  namespace: string;
  objectName: string;
}

export type SalesforceMetadataType = string;

export interface BaseElementProperties {
  id?: string; // optional due to a Salto bug on installed packages configurations
  type: SalesforceMetadataType;
  metadataId: string;
  name: string;
  label: string;
  namespace: string;
  description?: string;
  annotations?: AuditData; // optional because Salto don't always return it
  link?: string;
  objectName?: string;
  isActive?: boolean;
}

export interface CpqRecordProperties extends BaseElementProperties {
  objectName: string; // Required for CPQ records
}

export interface ApexTriggerProperties extends BaseElementProperties {
  objectName: string;
  type: typeof APEX_TRIGGER;
  isActive: boolean;
}

export interface ApexClassProperties extends BaseElementProperties {
  type: typeof APEX_CLASS;
  isActive: boolean;
}

export interface ValidationRuleProperties extends BaseElementProperties {
  type: typeof VALIDATION_RULE;
  objectName: string;
  isActive: boolean;
  errorMessage: string;
  formula: string;
}

export interface ApprovalProcessProperties extends BaseElementProperties {
  type: typeof APPROVAL_PROCESS;
  objectName: string;
  isActive: boolean;
}

export interface WorkflowRuleProperties extends BaseElementProperties {
  type: typeof WORKFLOW_RULE;
  objectName: string;
  isActive: boolean;
}

export type RecordTriggeredFlowProperties = {
  runWhen: string;
  triggerOn: string;
};

export type FlowSchedule = { frequency: string; startDate: string; startTime: string };

export type ScheduleTriggeredFlowProperties = { schedule: FlowSchedule };

// TODO SWEEP-8117 - Separate to more accurate interfaces, and we can probably remove triggers and actions
export interface FlowProperties extends BaseElementProperties {
  type: typeof FLOW;
  objectName?: string; // some flows can be object-based (RecordTriggered flows or process builder flows, for instance), but not all
  isActive: boolean;
  processType: string;
  flowType?: FlowTypes; // this is optional because process builder flows don't have a flow type
  triggers?: RecordTriggeredFlowProperties[] | ScheduleTriggeredFlowProperties[];
  actions?: string[];
}

export interface LayoutProperties extends BaseElementProperties {
  type: typeof LAYOUT;
  objectName: string;
  buttons: { name: string; label: string }[]; // TODO SWEEP-8247 - change to NameProperties
  relatedLists: RelatedListProperties[];
}

export type RelatedListProperties = { objectName: string; name: string; label: string };

export interface PathProperties extends BaseElementProperties {
  type: typeof PATH_ASSISTANT;
  objectName: string;
  fieldName: string;
  recordTypeName: string;
  isActive: boolean;
}

export interface EmailTemplateProperties extends BaseElementProperties {
  type: typeof EMAIL_TEMPLATE;
}

export interface ReportProperties extends BaseElementProperties {
  type: typeof REPORT;
}

export interface RecordTypeProperties extends BaseElementProperties {
  type: typeof RECORD_TYPE;
  objectName: string;
  isActive: boolean;
}

export function isCpqType(objectName: string): boolean {
  return (
    CPQ_NAMESPACE_PREFIXES.some((prefix) => objectName.startsWith(prefix)) ||
    CPQ_OBJECTS.includes(objectName)
  );
}

export type ConsumersOrDependencies = {
  [type: SalesforceMetadataType]: ConfigurationOrFieldProperties[];
};

export type SearchResult = {
  [type: SalesforceMetadataType]: ElementProperties[] | undefined;
};

export type MatchData = { match: string };

/**
 * Shared base interface for all documentation items
 * across org types (Salesforce, Snowflake, future platforms).
 */
export interface DocumentationItem {
  id: string;
  name: string;
  label: string;
  type: string;
  parentType: string;
  metadataId: string;
  match?: string;
}

// ---------------------------------------------------------------------------
// Generic documentation response types shared across platforms
// ---------------------------------------------------------------------------

export interface DocumentationListElementsResponse<T> {
  elements: T[];
  count: number;
}

export type DocumentationSearchResponse<T> = Record<string, T[]>;

export interface DocumentationDependenciesResponse<T> {
  dependencies: Record<string, T[]>;
}

export interface DocumentationDependentsResponse<T> {
  dependents: Record<string, T[]>;
}

export interface DocumentationChatRequest {
  message: string;
  sessionId: string;
  isFirstMessage: boolean;
}

export interface DocumentationChatResponse {
  message: string;
  sessionId: string;
}

export interface DocumentationSummaryResponse {
  summary: string;
}

export interface DocumentationSourceResponse {
  /** Full source text for the documentation panel (DDL/code when present, then rendered metadata). */
  body: string;
}

export interface DocumentationFindByNameResult {
  metadataId: string;
  type: string;
}

export function extractNameFromMetadataId(metadataId: string): string {
  const parts = metadataId.split('.');
  return parts[parts.length - 1] ?? metadataId;
}

// ContentSearchResult type "extends" the SearchResult type by adding MatchData to each array item type in the object values
// e.g: the type of the array items under the key [ParserKeys.APEX_CLASSES] is the intersection of
// the existing type - ApexTriggerProperties and the new type - MatchData
export type ContentSearchResult = {
  [key in keyof SearchResult]: (NonNullable<SearchResult[key]>[number] & MatchData)[];
};

export const isItemActive = (item: any): boolean => ('isActive' in item ? item.isActive : true);
